import * as React from 'react';
import { ListChecks } from 'lucide-react';
import { usePermission } from '@tyndo/hooks';
import { WidgetCard, WidgetStateView } from '../widget-card';
import { useWidgetData } from '../widget-contract';
import type { WidgetDescriptor, WidgetSize } from '../widget-contract';
import {
  toneBadgeClass,
  type WorkQueueModel,
  type WorkQueueItem,
  type WidgetPermissionRef,
} from './models';

/**
 * `CanonicalWorkQueueWidget` — the reusable **work-queue** kind.
 *
 * Renders a pending-item count plus a top-N drill-through list. The caller
 * supplies the descriptor (REAL endpoint + drill-through), the permission, and
 * a `select` projector mapping the real API response to a `WorkQueueModel`.
 *
 * States derive from `useWidgetData` over the real query — the empty state is a
 * genuine "queue clear", not a hidden failure.
 */
export interface CanonicalWorkQueueWidgetProps<TRaw> {
  descriptor: WidgetDescriptor;
  permission: WidgetPermissionRef;
  select: (raw: TRaw) => WorkQueueModel;
  isEmpty?: (raw: TRaw | null | undefined) => boolean;
  widgetSize?: WidgetSize;
  icon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
}

function QueueRow({ item }: { item: WorkQueueItem }): React.ReactElement {
  return (
    <li
      className="flex items-center justify-between gap-2"
      data-testid={`work-queue-row-${item.id}`}
    >
      {item.badge ? (
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${toneBadgeClass(item.tone)}`}
        >
          {item.badge}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate font-mono text-xs" title={item.primary}>
        {item.primary}
      </span>
      {item.secondary ? (
        <span className="shrink-0 text-[11px] text-muted-foreground">{item.secondary}</span>
      ) : null}
    </li>
  );
}

export function CanonicalWorkQueueWidget<TRaw>({
  descriptor,
  permission,
  select,
  isEmpty,
  widgetSize = 'small',
  icon,
  emptyTitle = 'Queue clear',
  emptyDescription = 'Nothing is pending in this queue.',
}: CanonicalWorkQueueWidgetProps<TRaw>): React.ReactElement {
  const perm = usePermission(permission.resource, permission.action);

  const state = useWidgetData<TRaw, WorkQueueModel>({
    widgetId: descriptor.widgetId,
    dataSource: descriptor.dataSource,
    cachePolicy: 'memory',
    permissionDenied: !perm.loading && !perm.allowed,
    project: (raw) => select(raw as TRaw),
    isEmpty: isEmpty as ((d: TRaw | null | undefined) => boolean) | undefined,
  });

  return (
    <WidgetCard
      descriptor={descriptor}
      source={state.status === 'ready' ? state.source : undefined}
      onRefresh={state.refetch}
      icon={icon ?? <ListChecks className="h-4 w-4" aria-hidden />}
    >
      <WidgetStateView
        state={state}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        onRetry={state.refetch}
      >
        {(data) => (
          <div className="flex h-full flex-col gap-3" data-testid="work-queue-body">
            <div>
              <div className="text-3xl font-bold tabular-nums" data-testid="work-queue-total">
                {data.total}
              </div>
              <div className="text-xs text-muted-foreground">{data.unit ?? 'pending'}</div>
            </div>
            {widgetSize !== 'small' && data.items.length > 0 ? (
              <ul className="flex flex-col gap-1" data-testid="work-queue-list">
                {data.items.map((item) => (
                  <QueueRow key={item.id} item={item} />
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </WidgetStateView>
    </WidgetCard>
  );
}

export default CanonicalWorkQueueWidget;
